/**
 * Report Builder Page
 * ====================
 * Full-page report builder with customizable/sizeable/moveable card views.
 * Shows Cometly and Builder Prime data based on user permissions.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Filter,
  LayoutGrid,
  Save,
  Check,
  Loader2,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  BarChart3,
  LineChart,
  AreaChart,
  PieChart,
  Table2,
  Hash,
  RefreshCw,
  Calendar,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportBuilderModal from '../components/marketing/ReportBuilderModal';
import MarketingReportWidget from '../components/marketing/MarketingReportWidget';

const API = process.env.REACT_APP_BACKEND_URL;

// Date range options
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_14_days', label: 'Last 14 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'ytd', label: 'Year to Date' },
];

// Map short date range values to full format for reports API
const mapDateRangeForReports = (shortValue) => {
  const mapping = {
    'today': 'today',
    'yesterday': 'yesterday',
    'last_7_days': 'last_7_days',
    'last_14_days': 'last_14_days',
    'last_30_days': 'last_30_days',
    'last_90_days': 'last_90_days',
    'this_week': 'this_week',
    'last_week': 'last_week',
    'this_month': 'this_month',
    'last_month': 'last_month',
    'this_quarter': 'this_quarter',
    'this_year': 'this_year',
    'ytd': 'ytd',
  };
  return mapping[shortValue] || shortValue;
};

// Get visualization icon
const getVisualizationIcon = (type) => {
  const icons = {
    bar: BarChart3,
    line: LineChart,
    area: AreaChart,
    pie: PieChart,
    table: Table2,
    number: Hash,
  };
  const Icon = icons[type] || BarChart3;
  return <Icon className="h-4 w-4" />;
};

const ReportBuilder = () => {
  const { user } = useAuth();
  
  // State
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  
  // Layout state
  const [layout, setLayout] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const containerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  
  // Observe container width
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/reports`);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  }, []);
  
  // Fetch dashboard layout
  const fetchLayout = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/marketing/dashboard/layout?page=report_builder`);
      const layoutData = response.data;
      
      setWidgets(layoutData.widgets || []);
      
      // Convert widgets to react-grid-layout format
      const gridLayout = (layoutData.widgets || []).map(w => ({
        i: w.id,
        x: w.x || 0,
        y: w.y || 0,
        w: w.w || 6,
        h: w.h || 4,
        minW: w.min_w || 2,
        minH: w.min_h || 2,
        maxW: w.max_w || 12,
        maxH: w.max_h || 8,
      }));
      
      setLayout(gridLayout);
    } catch (error) {
      console.error('Error fetching dashboard layout:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchReports();
    fetchLayout();
  }, [fetchReports, fetchLayout, dashboardRefreshKey]);
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // Save layout to backend
  const saveLayout = useCallback(async (updatedWidgets) => {
    setSaving(true);
    setSaveStatus('saving');
    
    try {
      await axios.post(`${API}/api/marketing/dashboard/layout?page=report_builder`, {
        widgets: updatedWidgets
      });
      setSaveStatus('saved');
      
      // Reset status after 2 seconds
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving layout:', error);
      setSaveStatus('error');
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  }, []);
  
  // Handle layout change
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    
    // Update widgets with new positions
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.id);
      if (layoutItem) {
        return {
          ...widget,
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h,
        };
      }
      return widget;
    });
    
    setWidgets(updatedWidgets);
    saveLayout(updatedWidgets);
  }, [widgets, saveLayout]);
  
  // Handle report created/updated
  const handleReportCreated = (report, addedToPage) => {
    fetchReports();
    if (addedToPage) {
      fetchLayout();
      setDashboardRefreshKey(prev => prev + 1);
    }
  };
  
  // Handle edit report
  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowReportBuilder(true);
  };
  
  // Handle duplicate report
  const handleDuplicateReport = async (report) => {
    try {
      await axios.post(`${API}/api/marketing/reports/${report.id}/duplicate`);
      toast.success('Report duplicated');
      fetchReports();
      fetchLayout();
      setDashboardRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error duplicating report:', err);
      toast.error('Failed to duplicate report');
    }
  };
  
  // Handle delete widget
  const handleDeleteWidget = async (widgetId) => {
    try {
      // Remove from layout
      const updatedWidgets = widgets.filter(w => w.id !== widgetId);
      setWidgets(updatedWidgets);
      setLayout(prev => prev.filter(l => l.i !== widgetId));
      
      // Save updated layout
      await saveLayout(updatedWidgets);
      toast.success('Widget removed from page');
    } catch (error) {
      console.error('Error removing widget:', error);
      toast.error('Failed to remove widget');
    }
  };
  
  // Handle delete report completely
  const handleDeleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/api/marketing/reports/${reportId}`);
      toast.success('Report deleted');
      fetchReports();
      fetchLayout();
      setDashboardRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting report:', err);
      toast.error('Failed to delete report');
    }
  };
  
  // Filter reports by search
  const filteredReports = reports.filter(report => 
    report.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.metrics?.some(m => m.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Calculate grid columns (12 column layout)
  const cols = 12;
  const rowHeight = 80;
  
  return (
    <Layout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Report Builder</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create and customize your marketing reports from Cometly and Builder Prime data
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Save Status Indicator */}
            {saveStatus && (
              <div className="flex items-center gap-2 text-sm">
                {saveStatus === 'saving' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-green-500">Saved</span>
                  </>
                )}
              </div>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchReports();
                fetchLayout();
                setDashboardRefreshKey(prev => prev + 1);
              }}
              data-testid="refresh-reports-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            
            <Button
              onClick={() => {
                setEditingReport(null);
                setShowReportBuilder(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-report-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Report
            </Button>
          </div>
        </div>
        
        {/* Filter Bar */}
        <Card className="p-4 bg-muted/30">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-reports-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGES.map(range => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Report Count */}
            <div className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
              <Badge variant="outline">
                {widgets.length} widgets on page
              </Badge>
              <Badge variant="secondary">
                {reports.length} total reports
              </Badge>
            </div>
          </div>
        </Card>
        
        {/* Dashboard Grid */}
        <div ref={containerRef} className="relative min-h-[600px]">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : widgets.length > 0 ? (
            <GridLayout
              className="layout"
              layout={layout}
              cols={cols}
              rowHeight={rowHeight}
              width={containerWidth}
              onLayoutChange={handleLayoutChange}
              draggableHandle=".drag-handle"
              isResizable={true}
              isDraggable={true}
              margin={[16, 16]}
              containerPadding={[0, 0]}
              useCSSTransforms={true}
            >
              {widgets.map(widget => {
                const report = reports.find(r => r.id === widget.report_id);
                
                return (
                  <div key={widget.id} className="relative group">
                    <Card className="h-full overflow-hidden">
                      {/* Widget Header */}
                      <div className="drag-handle flex items-center justify-between px-4 py-2 border-b bg-muted/30 cursor-move">
                        <div className="flex items-center gap-2">
                          {getVisualizationIcon(widget.visualization_type)}
                          <span className="font-medium text-sm truncate max-w-[200px]">
                            {widget.name || report?.name || 'Untitled Report'}
                          </span>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => report && handleEditReport(report)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Report
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => report && handleDuplicateReport(report)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteWidget(widget.id)}
                              className="text-orange-600"
                            >
                              <X className="h-4 w-4 mr-2" />
                              Remove from Page
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => report && handleDeleteReport(report.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Report
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {/* Widget Content */}
                      <div className="p-4 h-[calc(100%-44px)] overflow-auto">
                        <MarketingReportWidget
                          widget={widget}
                          report={report}
                          onEdit={() => report && handleEditReport(report)}
                          onDuplicate={() => report && handleDuplicateReport(report)}
                          onRemove={() => handleDeleteWidget(widget.id)}
                          onRefresh={() => setDashboardRefreshKey(prev => prev + 1)}
                          dateRangeFilter={mapDateRangeForReports(dateRange)}
                        />
                      </div>
                    </Card>
                  </div>
                );
              })}
            </GridLayout>
          ) : (
            <Card className="p-12 text-center">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No reports on this page yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create your first report to visualize your marketing data from Cometly and Builder Prime.
                Reports can be customized, resized, and rearranged.
              </p>
              <Button
                onClick={() => {
                  setEditingReport(null);
                  setShowReportBuilder(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Report
              </Button>
            </Card>
          )}
        </div>
        
        {/* Available Reports Section */}
        {reports.length > 0 && widgets.length === 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Available Reports</h3>
            <p className="text-sm text-muted-foreground">
              Click on a report to add it to this page. You can then resize and rearrange.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredReports.map(report => (
                <Card 
                  key={report.id} 
                  className="p-4 cursor-pointer hover:border-primary transition-colors"
                  onClick={() => handleEditReport(report)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {getVisualizationIcon(report.visualization_type)}
                    <span className="font-medium">{report.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {report.metrics?.length || 0} metrics • {report.date_range?.preset || 'Custom range'}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Data Source Info */}
        <div className="text-center text-xs text-muted-foreground pt-4 border-t">
          <p>Data sourced from Cometly + Builder Prime • Drag widgets to rearrange • Resize from corners</p>
        </div>
      </div>
      
      {/* Report Builder Modal */}
      <ReportBuilderModal
        isOpen={showReportBuilder}
        onClose={() => {
          setShowReportBuilder(false);
          setEditingReport(null);
        }}
        onReportCreated={handleReportCreated}
        editingReport={editingReport}
        targetPage="report_builder"
      />
    </Layout>
  );
};

export default ReportBuilder;
